import React, { useEffect, useContext, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toJS } from 'mobx';
import FadeIn from 'react-fade-in';
import { Observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import { GlobalHotKeys } from 'react-hotkeys';
import moment from 'moment';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModal';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import { quickDrawerFocus } from '../../_shared/QuickDrawer';

import CustomerUpdateStore from '../../../../stores/CustomerUpdateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as rts from '../../../../constants/routes';
import * as fn from '../../../../utilities/_functions';
import * as ch from '../../../../utilities/customerHelper';
import * as ph from '../../../../utilities/personHelper';
import * as oh from '../../../../utilities/operationHelper';
import * as ah from '../../../../utilities/addressHelper';

import api from '../../../../api';

import './LinkPrivateInsurance.scss';

function LinkPrivateInsurance(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const focusTimer = useRef(null);
    const customer = useContext(CustomerUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [confirmUpdate, setConfirmUpdate] = useState(false);
    const [relatedPrivateInsurances, setRelatedPrivateInsurances] = useState([]);
    const [selectedPrivateInsuranceIds, setSelectedPrivateInsuranceIds] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        if (props && props.extraProps && props.extraProps.privateInsurances && props.extraProps.privateInsurances.length > 0) {
            const uniquePrivateInsurances = props.extraProps.privateInsurances.reduce((array, item) => {
                if (!array.some(a => a.privateInsurancePolicyId === item.privateInsurancePolicyId) && !item.members.some(i => i.id === customer.data.id)) {
                    array.push(item);
                }
                return array;
            }, []);
            setRelatedPrivateInsurances(uniquePrivateInsurances);
        }

        focusTimer.current = setTimeout(() => {
            quickDrawerFocus(props.drawer);
        }, 100)

        return () => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleLinkPrivateInsurance = (event, privateInsurance) => {
        const newLinkIds = [...selectedPrivateInsuranceIds];
        const index = newLinkIds.findIndex(l => l === privateInsurance.privateInsurancePolicyId);

        if (index < 0) {
            newLinkIds.push(privateInsurance.privateInsurancePolicyId);
        }
        else {
            newLinkIds.splice(index, 1);
        }

        setSelectedPrivateInsuranceIds(newLinkIds);
        setHasUnsavedChanges(true);
    }

    const handleNewInsurance = () => {
        if (isMounted.current) {
            quickDrawer.activateQuickDrawer('customer', 'private-insurance-create', null, handleNewPrivateInsuranceSuccess, handleNewPrivateInsuranceCancel);
        }
    }

    const handleNewPrivateInsuranceSuccess = (result) => {
        if (result && result.updated) {
            customer.clearNewPrivateInsurance();
            toast.dark(() => <p data-ins-upd>Customer's insurance updated.</p>);

            if (props.onCancel && fn.isFunction(props.onCancel)) {
                props.onCancel();
            }
        }
    }

    const handleNewPrivateInsuranceCancel = () => {
        customer.clearNewPrivateInsurance();
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        if (hasUnsavedChanges && selectedPrivateInsuranceIds && selectedPrivateInsuranceIds.length > 0) {
            setConfirmUpdate(true);
        }
    }

    const handleConfirmUpdate = (event) => {
        if (selectedPrivateInsuranceIds && selectedPrivateInsuranceIds.length > 0) {
            const options = relatedPrivateInsurances.reduce((array, item) => {
                if (selectedPrivateInsuranceIds.some(id => id === item.privateInsurancePolicyId)) {
                    const option = {
                        customerId: item.customer.id,
                        data: {
                            privateInsurancePolicyId: item.privateInsurancePolicyId,
                            privateInsuranceProviderId: item.privateInsuranceProviderId,
                            primaryPolicyHolderId: item.privateInsurancePolicyId,
                            policyHolderName: item.policyHolderName,
                            memberId: item.memberId,
                            planNumber: item.planNumber,
                            accountNumber: item.accountNumber,
                            policyNumber: item.policyNumber,
                            noteHtml: item.noteHtml,
                            members: [...[{ id: customer.data.id }], ...item.members.map(m => { return { id: m.id } })],
                            displayOrder: item.displayOrder,
                        }
                    }
                    array.push(option);
                }
                return array;
            }, []);
            const requests = options.map((option => {
                return api.CustomerPrivateInsurances.update(
                    option.customerId, option.data
                )
            }));

            Promise.all(requests)
                .then(() => {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, { updated: true, data: null });
                    }
                    if (isMounted.current) {
                        setConfirmUpdate(false);
                    }
                })
                .catch(() => {
                    toast.error(() => <p>Oops, something went wrong. Try again later.</p>, { position: 'top-center' });
                })
        }
    }

    const handleCancelUpdate = () => {
        if (isMounted.current) {
            setConfirmUpdate(false);
        }
    }

    const renderCustomer = () => {
        return customer && customer.data ? <div
            className='profile-wrapper'
        >
            <div className='profile'>
                <span
                    className={`profile-image profile-initials rounded-circle d-flex text-white ${ch.getProfileBgColor(customer.data)} fw-500`}
                    title={ph.getFullName(customer.data)}
                >
                    {customer.data.initials}
                </span>
            </div>
            <div className='description flex-1'>
                <Link to={`${rts.Customers.Home}/${customer.data.id}`} className='name text-gray-700' onClick={quickDrawer.deactivateAll}>{ph.getFullName(customer.data, true)}
                    {
                        customer.data.dateOfBirth || customer.data.sex || customer.data.gender || customer.data.pronoun ?
                            <small className='text-nowrap ml-2'>({`${ph.getAge(customer.data.dateOfBirth)} ${ph.getSexGenderPronounDisplay(customer.data)}`.trim()})</small> : null
                    }
                </Link>
                {
                    customer.data.address && customer.data.address.country ?
                        <div className='info'>{ah.getAddressHtml(customer.data.address)}</div> : null
                }
            </div>
        </div> : null
    }

    const getOtherPlanMemberNames = () => {
        let sortedMembers = [];

        if (selectedPrivateInsuranceIds && selectedPrivateInsuranceIds.length > 0) {
            const selectedPrivateInsurances = relatedPrivateInsurances.filter(p => selectedPrivateInsuranceIds.some(id => id === p.privateInsurancePolicyId));
            sortedMembers = selectedPrivateInsurances.reduce((array, item) => {
                if (!array.some(a => a.id === item.customer.id)) {
                    array.push(toJS(item.customer));
                }
                if (item.members && item.members.length > 0) {
                    for (let mi = 0; mi < item.members.length; mi++) {
                        if (!array.some(a => a.id === item.members[mi].id)) {
                            array.push(toJS(item.members[mi]));
                        }
                    }
                }
                return array;
            }, []).sort((a, b) => (a.dateOfBirth ? moment(a.dateOfBirth).unix() : 0) - (b.dateOfBirth ? moment(b.dateOfBirth).unix() : 0));
        }

        return [...new Set(sortedMembers.map(m => { return ph.getPreferredFirstName(m) }))].join(', ').replace(/,(?=[^,]*$)/, ' and');
    }

    return <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) && !confirmUpdate ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                handleCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <fieldset disabled={customer.isSaving}>
                <div className='update-customer-insurance-container quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('customer', 'private-insurance')}
                        action='Update Customer'
                        category='Insurance'
                        className='customers'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <Observer>{() =>
                            <div className='body-content'>
                                <FadeIn>
                                    <Observer>{() =>
                                        <section className='customer'>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-0'>
                                                        <label><small>Customer</small></label>
                                                        {renderCustomer()}
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    }</Observer>
                                    <section className='mt-3'>
                                        <div className='row'>
                                            <div className='col-12'>
                                                <div className='form-group mb-0'>
                                                    <label><small>Link as a plan member</small></label>
                                                    {
                                                        relatedPrivateInsurances.map((item, ri) => {
                                                            return <div
                                                                key={`link_private_insurance_${ri}`}
                                                                className='d-flex w-100 align-item-center'
                                                            >
                                                                <Observer>{() =>
                                                                    <>
                                                                        <div
                                                                            className='custom-control custom-checkbox mt-1 mr-2'
                                                                        >
                                                                            <input
                                                                                id={`link_private_insurance_policy_${ri}`}
                                                                                type='checkbox'
                                                                                name={`link-private-insurance-policy-${ri}`}
                                                                                className='custom-control-input'
                                                                                disabled={!ch.checkCanUpdate(customer.data)}
                                                                                checked={selectedPrivateInsuranceIds.some(id => id === item.privateInsurancePolicyId)}
                                                                                onChange={(e) => { handleLinkPrivateInsurance(e, item) }}
                                                                            />
                                                                            <label
                                                                                htmlFor={`link_private_insurance_policy_${ri}`}
                                                                                className='custom-control-label'
                                                                            ></label>
                                                                        </div>
                                                                        <div className='profile-wrapper pb-0'>
                                                                            <div className='description flex-1'>
                                                                                {
                                                                                    item.privateInsuranceProvider ?
                                                                                        <div className='name'><strong>{item.privateInsuranceProvider.name}</strong></div> : null
                                                                                }
                                                                                {
                                                                                    item.policyHolderName ?
                                                                                        <div className='info'><strong className='mr-1'>Primary holder:</strong>{item.policyHolderName}</div> :
                                                                                        <div className='info'><strong className='mr-1'>Patient:</strong>{ph.getFullName(item.customer)}</div>
                                                                                }
                                                                                {
                                                                                    item.memberId || item.planNumber || item.accountNumber || item.policyNumber ?
                                                                                        <div className='info'>{[item.memberId, item.planNumber, item.accountNumber, item.policyNumber].filter(i => !!i).join(' - ')}</div> : null
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                }</Observer>
                                                            </div>
                                                        })
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                </FadeIn>
                            </div>
                        }</Observer>
                    </div>
                    <div className='quick-drawer-action pl-3'>
                        <div className='row'>
                            <div className='col-4'>
                                {
                                    !props.extraProps || props.extraProps.allowAdd !== false ?
                                        <button
                                            type='button'
                                            className='btn btn-light'
                                            onClick={handleNewInsurance}
                                        >Add</button> : <></>
                                }
                            </div>
                            <div className='col-8'>
                                <div className='float-right'>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                        disabled={!ch.checkCanUpdate(customer.data) || selectedPrivateInsuranceIds.length === 0}
                                    >Link</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
        <BodyEnd>
            <ConfirmModal
                icon={<i className={'text-warning mr-2 ' + oh.getIcon('customer', 'private-insurance')}></i>}
                message={
                    <>
                        This will also update <span className='fw-500'>&nbsp;{getOtherPlanMemberNames()}'s insurance.</span>&nbsp;&nbsp;Continue?
                    </>
                }
                show={confirmUpdate}
                option1Text={'Continue'}
                cancelText={'Cancel'}
                option1ClassName='btn btn-warning shadow-0 bootbox-accept'
                onOption1Click={handleConfirmUpdate}
                onCancel={handleCancelUpdate}
            />
        </BodyEnd>
    </>
}

export default LinkPrivateInsurance;